import { useCallback } from 'react'

import { useAccount, usePublicClient } from 'wagmi'

import { useGasFetcher } from 'hooks/api'
import { usePrivateKeyWallet } from 'hooks/wallet'

import { usePrivateKey } from 'state/user/hooks'

import { parseErrorToUserReadableMessage } from 'utils/errors'

import { errors } from 'constants/errors'
import { etherNumbers } from 'constants/variables'

export function useRefund() {
  const { address } = useAccount()

  const privateKey = usePrivateKey()
  const getPrivateKeyWallet = usePrivateKeyWallet()

  const gasFetcher = useGasFetcher()
  const publicClient = usePublicClient()

  return useCallback(async () => {
    try {
      if (!privateKey) {
        throw new Error(errors.validation.PK_IS_REQUIRED)
      }
      const wallet = getPrivateKeyWallet(privateKey)

      const balance = await publicClient.getBalance({ address: wallet.account.address })
      const nonce = await publicClient.getTransactionCount({ address: wallet.account.address })

      const gasParams = await gasFetcher.getGasParams()
      const gasCost = gasParams?.maxFeePerGas || gasParams?.gasPrice

      if (!gasCost) {
        throw new Error(errors.validation.GAS_PARAMS_ARE_REQUIRED)
      }
      const gasLimit = etherNumbers.TRANSFER_GAS_LIMIT

      const value = balance - gasCost * gasLimit

      if (value <= etherNumbers.BG_ZERO) {
        return null
      }

      const txHash = await wallet.sendTransaction({ nonce, value, to: address, gas: gasLimit, ...gasParams })

      return { txHash, value, from: wallet.account.address }
    } catch (err) {
      throw new Error(parseErrorToUserReadableMessage(err))
    }
  }, [address, gasFetcher, privateKey, getPrivateKeyWallet, publicClient])
}
